import { DriveMode, EVMusicalState, EVSoundDebugInfo, EVSoundProfile, EVSoundState } from '../types';
import { DEFAULT_EV_PROFILE_ID, EV_SOUND_PROFILES } from '../config/evProfiles';

/**
 * Non-linear musical speed landmarks (km/h -> base frequency Hz)
 * 0 km/h: ~70 Hz (warm fundamental)
 * 30 km/h: ~90 Hz
 * 60 km/h: ~120 Hz
 * 100 km/h: ~180 Hz
 * 150 km/h: ~280 Hz
 * 200 km/h: ~420 Hz
 */
interface SpeedLandmark {
  speed: number;
  pitch: number;
}

const MUSICAL_SPEED_LANDMARKS: SpeedLandmark[] = [
  { speed: 0, pitch: 70 },
  { speed: 30, pitch: 90 },
  { speed: 60, pitch: 120 },
  { speed: 100, pitch: 180 },
  { speed: 150, pitch: 280 },
  { speed: 200, pitch: 420 },
];

/**
 * Map vehicle speed non-linearly to musical fundamental pitch
 */
export function mapSpeedToPitch(speedKmh: number, profile: EVSoundProfile): number {
  const s = Math.max(0, speedKmh);
  const baseScale = profile.baseFrequency / 70;

  // Exact landmark piecewise Hermite interpolation
  for (let i = 0; i < MUSICAL_SPEED_LANDMARKS.length - 1; i++) {
    const curr = MUSICAL_SPEED_LANDMARKS[i];
    const next = MUSICAL_SPEED_LANDMARKS[i + 1];
    if (s >= curr.speed && s <= next.speed) {
      const t = (s - curr.speed) / (next.speed - curr.speed);
      const smoothT = t * t * (3 - 2 * t);
      const interpolated = curr.pitch + (next.pitch - curr.pitch) * smoothT;
      return interpolated * baseScale;
    }
  }

  // Beyond 200 km/h: smooth musical continuation
  const topRatio = Math.min(1.4, s / 200);
  const beyondPitch = 420 * Math.pow(550 / 420, (topRatio - 1.0) / 0.4);
  return beyondPitch * baseScale;
}

/**
 * Map acceleration to acoustic drive intensity
 */
export function mapAccelerationToIntensity(acceleration: number, profile: EVSoundProfile): number {
  const normAccel = Math.max(0, Math.min(1.0, acceleration));
  return Math.min(1.0, normAccel * profile.accelerationSensitivity);
}

/**
 * Map braking input to deceleration expression
 */
export function mapBrakingToExpression(brake: number, profile: EVSoundProfile): number {
  const normBrake = Math.max(0, Math.min(1.0, brake));
  return Math.min(1.0, normBrake * profile.brakingSensitivity);
}

/**
 * Detect selective coasting and compute sound reduction factor.
 * Intelligently cuts artificial drone on the highway.
 */
export function calculateCoastingAmount(
  speedKmh: number,
  acceleration: number,
  throttle: number,
  brake: number
): number {
  if (speedKmh < 4.0) return 0.0;
  const accelSmall = Math.abs(acceleration) < 0.06;
  const throttleLow = throttle < 0.09;
  const brakeLow = brake < 0.08;

  if (accelSmall && throttleLow && brakeLow) {
    const accelCloseness = 1.0 - Math.min(1.0, Math.abs(acceleration) / 0.06);
    const throttleCloseness = 1.0 - Math.min(1.0, throttle / 0.09);
    return accelCloseness * 0.5 + throttleCloseness * 0.5;
  }
  return 0.0;
}

export class EVSoundEngine {
  private ctx: AudioContext;
  private destination: AudioNode;
  private profile: EVSoundProfile;
  private driveMode: DriveMode = 'COMFORT';
  private isRunning: boolean = false;

  // Master Gain, Saturation & Spatial Submix
  private masterGain: GainNode;
  private saturationNode: WaveShaperNode | null = null;
  private spatialPanner: StereoPannerNode | null = null;
  private spatialMixerGain: GainNode;

  // Pulse Amplitude Modulation (Layer E)
  private pulseLfo: OscillatorNode | null = null;
  private pulseLfoGain: GainNode;
  private pulseModGain: GainNode;

  // Relax Mode Massage LFO (Low-frequency sub-bass swell)
  private relaxMassageLfo: OscillatorNode | null = null;
  private relaxMassageGain: GainNode;

  // Layer 0: EV_SUB (Deep Sub-Octave Propulsion Foundation - 0.5x Pitch)
  private subOsc: OscillatorNode | null = null;
  private subFilter: BiquadFilterNode;
  private subGain: GainNode;

  // Layer 1: EV_CORE (Fundamental tone with lowpass warmth - 1.0x Pitch)
  private coreOsc1: OscillatorNode | null = null;
  private coreOsc2: OscillatorNode | null = null;
  private coreFilter: BiquadFilterNode;
  private coreGain: GainNode;

  // Layer 2: EV_HARMONICS Bank (Up to 8 harmonic voices - Multi-Pitch Chord Ratios)
  private harmonicOscs: OscillatorNode[] = [];
  private harmonicGains: GainNode[] = [];
  private harmonicsSubmix: GainNode;

  // Layer 3: EV_PAD (Slow-moving detuned cinematic pad - 0.5x / 1.0x Pitch)
  private padOsc1: OscillatorNode | null = null;
  private padOsc2: OscillatorNode | null = null;
  private padFilter: BiquadFilterNode;
  private padFilterLfo: OscillatorNode | null = null;
  private padFilterLfoGain: GainNode;
  private padSubmix: GainNode;

  // Layer 4: EV_CHOIR (5 detuned synthetic voices with dual formants - 2.0x Pitch)
  private choirOscs: OscillatorNode[] = [];
  private choirGains: GainNode[] = [];
  private choirSubmix: GainNode;
  private choirFormant1: BiquadFilterNode;
  private choirFormant2: BiquadFilterNode;
  private choirLfo: OscillatorNode | null = null;
  private choirLfoGain: GainNode;

  // Layer 5: EV_AIR (Filtered looping pink noise)
  private airNoiseSource: AudioBufferSourceNode | null = null;
  private airFilter: BiquadFilterNode;
  private airGain: GainNode;

  // Layer 6: EV_ACCELERATION (Harmonic tension shimmer - 3.8x Pitch)
  private accelOsc: OscillatorNode | null = null;
  private accelFilter: BiquadFilterNode;
  private accelGain: GainNode;

  // Layer 7: EV_REGEN / EV_BRAKE (Descending harmonic gesture and inverter tone)
  private regenOsc: OscillatorNode | null = null;
  private regenFilter: BiquadFilterNode;
  private regenGain: GainNode;

  // Layer 8: EV_INVERTER (High-Frequency Plasma Turbine & Singing Inverter - 6.0x-8.0x Pitch / 2-5 kHz)
  private inverterOsc: OscillatorNode | null = null;
  private plasmaOsc: OscillatorNode | null = null;
  private inverterFilter: BiquadFilterNode;
  private inverterGain: GainNode;

  // Musical & Dynamic State
  private currentSpeedKmh: number = 0;
  private currentAcceleration: number = 0;
  private currentThrottle: number = 0;
  private currentBrake: number = 0;
  private currentBaseFreqHz: number = 70;
  private currentIntensity: number = 0;
  private currentTension: number = 0;
  private currentSpatialWidth: number = 0;
  private currentCoastingAmount: number = 0;
  private currentChordState: string = 'Warm Fundamental';
  private currentPulseRateHz: number = 1.2;
  private speedStabilityTimerMs: number = 0;
  private lastSpeedSample: number = 0;
  private lastUpdateTime: number = 0;

  constructor(
    ctx: AudioContext,
    destination: AudioNode,
    initialProfileId: string = DEFAULT_EV_PROFILE_ID,
    initialMode: DriveMode = 'COMFORT'
  ) {
    this.ctx = ctx;
    this.destination = destination;
    const resolvedProfileId = (EV_SOUND_PROFILES[initialProfileId as keyof typeof EV_SOUND_PROFILES]
      ? initialProfileId
      : DEFAULT_EV_PROFILE_ID) as keyof typeof EV_SOUND_PROFILES;
    this.profile = EV_SOUND_PROFILES[resolvedProfileId] || EV_SOUND_PROFILES.ev_cinematic;
    this.driveMode = initialMode;

    const now = this.ctx.currentTime;

    // 1. Master Output Gain
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.setValueAtTime(0.0001, now);

    // 2. Analog Warmth Saturation Waveshaper (normalized soft-clip, zero volume loss)
    try {
      this.saturationNode = this.ctx.createWaveShaper();
      this.saturationNode.curve = this.createSaturationCurve(1.4) as Float32Array<ArrayBuffer>;
      this.saturationNode.oversample = '2x';
      this.saturationNode.connect(this.masterGain);
    } catch {
      this.saturationNode = null;
    }

    const preMasterNode = this.saturationNode || this.masterGain;

    // 3. Spatial Width Mixer & Panner
    this.spatialMixerGain = this.ctx.createGain();
    this.spatialMixerGain.gain.setValueAtTime(1.0, now);

    // Pulse AM Modulator Node
    this.pulseModGain = this.ctx.createGain();
    this.pulseModGain.gain.setValueAtTime(1.0, now);

    this.pulseLfoGain = this.ctx.createGain();
    this.pulseLfoGain.gain.setValueAtTime(0.0, now);

    if (typeof this.ctx.createStereoPanner === 'function') {
      try {
        this.spatialPanner = this.ctx.createStereoPanner();
        this.spatialPanner.pan.setValueAtTime(0, now);
        this.spatialMixerGain.connect(this.spatialPanner);
        this.spatialPanner.connect(this.pulseModGain);
      } catch {
        this.spatialMixerGain.connect(this.pulseModGain);
      }
    } else {
      this.spatialMixerGain.connect(this.pulseModGain);
    }
    this.pulseModGain.connect(preMasterNode);
    this.masterGain.connect(this.destination);

    // 4. Relax Mode Massage LFO Gain
    this.relaxMassageGain = this.ctx.createGain();
    this.relaxMassageGain.gain.setValueAtTime(0.0, now);

    // 5. Layer 0: EV_SUB (0.5x Sub-Octave Cinema Bass Foundation)
    this.subFilter = this.ctx.createBiquadFilter();
    this.subFilter.type = 'lowpass';
    this.subFilter.frequency.setValueAtTime(200, now);
    this.subGain = this.ctx.createGain();
    this.subGain.gain.setValueAtTime(0.35, now);
    this.subGain.connect(this.subFilter);
    this.subFilter.connect(this.spatialMixerGain);

    // 6. Layer 1: EV_CORE (1.0x Fundamental Stator Tone)
    this.coreFilter = this.ctx.createBiquadFilter();
    this.coreFilter.type = 'lowpass';
    this.coreFilter.frequency.setValueAtTime(950, now);
    this.coreGain = this.ctx.createGain();
    this.coreGain.gain.setValueAtTime(0.85, now);
    this.coreGain.connect(this.coreFilter);
    this.coreFilter.connect(this.spatialMixerGain);

    // 7. Layer 2: EV_HARMONICS Bank
    this.harmonicsSubmix = this.ctx.createGain();
    this.harmonicsSubmix.gain.setValueAtTime(0.85, now);
    this.harmonicsSubmix.connect(this.spatialMixerGain);

    // 7. Layer 3: EV_PAD (Cinematic Detuned Pad)
    this.padSubmix = this.ctx.createGain();
    this.padSubmix.gain.setValueAtTime(0.70, now);
    this.padFilter = this.ctx.createBiquadFilter();
    this.padFilter.type = 'lowpass';
    this.padFilter.frequency.setValueAtTime(800, now);
    this.padFilter.Q.setValueAtTime(2.4, now);
    this.padFilterLfoGain = this.ctx.createGain();
    this.padFilterLfoGain.gain.setValueAtTime(180, now);

    this.padSubmix.connect(this.padFilter);
    this.padFilter.connect(this.spatialMixerGain);

    // 8. Layer 4: EV_CHOIR Submix & Dual Formants
    this.choirSubmix = this.ctx.createGain();
    this.choirSubmix.gain.setValueAtTime(0.60, now);
    this.choirFormant1 = this.ctx.createBiquadFilter();
    this.choirFormant1.type = 'peaking';
    this.choirFormant1.frequency.setValueAtTime(720, now);
    this.choirFormant1.Q.setValueAtTime(4.0, now);
    this.choirFormant1.gain.setValueAtTime(6.0, now);

    this.choirFormant2 = this.ctx.createBiquadFilter();
    this.choirFormant2.type = 'peaking';
    this.choirFormant2.frequency.setValueAtTime(1850, now);
    this.choirFormant2.Q.setValueAtTime(3.8, now);
    this.choirFormant2.gain.setValueAtTime(5.5, now);

    this.choirLfoGain = this.ctx.createGain();
    this.choirLfoGain.gain.setValueAtTime(0.05, now);

    this.choirSubmix.connect(this.choirFormant1);
    this.choirFormant1.connect(this.choirFormant2);
    this.choirFormant2.connect(this.spatialMixerGain);

    // 9. Layer 5: EV_AIR Texture
    this.airFilter = this.ctx.createBiquadFilter();
    this.airFilter.type = 'bandpass';
    this.airFilter.frequency.setValueAtTime(2400, now);
    this.airFilter.Q.setValueAtTime(1.8, now);
    this.airGain = this.ctx.createGain();
    this.airGain.gain.setValueAtTime(0.0, now);
    this.airFilter.connect(this.airGain);
    this.airGain.connect(this.spatialMixerGain);

    // 10. Layer 6: EV_ACCELERATION Shimmer
    this.accelFilter = this.ctx.createBiquadFilter();
    this.accelFilter.type = 'highpass';
    this.accelFilter.frequency.setValueAtTime(1050, now);
    this.accelGain = this.ctx.createGain();
    this.accelGain.gain.setValueAtTime(0.0, now);
    this.accelFilter.connect(this.accelGain);
    this.accelGain.connect(this.spatialMixerGain);

    // 11. Layer 7: EV_REGEN / EV_BRAKE Deceleration Tone
    this.regenFilter = this.ctx.createBiquadFilter();
    this.regenFilter.type = 'lowpass';
    this.regenFilter.frequency.setValueAtTime(950, now);
    this.regenGain = this.ctx.createGain();
    this.regenGain.gain.setValueAtTime(0.0, now);
    this.regenFilter.connect(this.regenGain);
    this.regenGain.connect(this.spatialMixerGain);

    // 12. Layer 8: EV_INVERTER (Singing Inverter Whine / Plasma Turbine Overtones)
    this.inverterFilter = this.ctx.createBiquadFilter();
    this.inverterFilter.type = 'bandpass';
    this.inverterFilter.frequency.setValueAtTime(2800, now);
    this.inverterFilter.Q.setValueAtTime(3.2, now);
    this.inverterGain = this.ctx.createGain();
    this.inverterGain.gain.setValueAtTime(0.0, now);
    this.inverterGain.connect(this.inverterFilter);
    this.inverterFilter.connect(this.spatialMixerGain);
  }

  /**
   * Generates smooth analog saturation curve with normalized unity gain
   */
  private createSaturationCurve(k: number = 1.4): Float32Array {
    const n_samples = 2048;
    const curve = new Float32Array(n_samples);
    const norm = Math.tanh(k);
    for (let i = 0; i < n_samples; ++i) {
      const x = (i * 2) / n_samples - 1;
      curve[i] = Math.tanh(k * x) / norm;
    }
    return curve;
  }

  /**
   * Start EV Sound Synthesis
   */
  public start(): void {
    if (this.isRunning) return;
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    this.isRunning = true;

    const now = this.ctx.currentTime;
    this.buildNodes(now);

    // Authoritative full master volume un-mute
    this.masterGain.gain.setTargetAtTime(1.0, now, 0.15);
    this.update(0, 0, 0, 0, this.driveMode, performance.now());
  }

  public getIsRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Stop EV Sound Synthesis cleanly
   */
  public stop(): void {
    if (!this.isRunning) return;
    this.isRunning = false;

    const now = this.ctx.currentTime;
    this.masterGain.gain.setTargetAtTime(0.0001, now, 0.08);

    setTimeout(() => {
      this.cleanupNodes();
    }, 150);
  }

  /**
   * Allocate continuous audio nodes once
   */
  private buildNodes(now: number): void {
    this.cleanupNodes();

    // 0. Layer 0: EV_SUB (0.5x Sub-Octave Propulsion Foundation)
    const sOsc = this.ctx.createOscillator();
    sOsc.type = 'sine';
    sOsc.frequency.setValueAtTime(this.profile.baseFrequency * 0.5, now);
    sOsc.connect(this.subGain);
    sOsc.start(now);
    this.subOsc = sOsc;

    // 1. Layer 1: EV_CORE
    const c1 = this.ctx.createOscillator();
    c1.type = 'sine';
    c1.frequency.setValueAtTime(this.profile.baseFrequency, now);
    c1.connect(this.coreGain);
    c1.start(now);
    this.coreOsc1 = c1;

    const c2 = this.ctx.createOscillator();
    c2.type = 'triangle';
    c2.frequency.setValueAtTime(this.profile.baseFrequency, now);
    c2.detune.setValueAtTime(4, now);
    c2.connect(this.coreGain);
    c2.start(now);
    this.coreOsc2 = c2;

    // 2. Layer 2: EV_HARMONICS Bank (8 Voices)
    this.harmonicOscs = [];
    this.harmonicGains = [];
    const maxHarmonics = 8;
    for (let i = 0; i < maxHarmonics; i++) {
      const osc = this.ctx.createOscillator();
      osc.type = i % 2 === 0 ? 'sine' : 'triangle';
      const ratio = this.profile.harmonicRatios[i] || (i + 1);
      osc.frequency.setValueAtTime(this.profile.baseFrequency * ratio, now);
      osc.detune.setValueAtTime((i % 2 === 0 ? 1 : -1) * (i * 2 + 1), now);

      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0, now);
      osc.connect(g);
      g.connect(this.harmonicsSubmix);
      osc.start(now);

      this.harmonicOscs.push(osc);
      this.harmonicGains.push(g);
    }

    // 3. Layer 3: EV_PAD (2 Detuned Oscillators + Filter LFO)
    const p1 = this.ctx.createOscillator();
    p1.type = 'sawtooth';
    p1.frequency.setValueAtTime(this.profile.baseFrequency, now);
    p1.detune.setValueAtTime(-9, now);
    p1.connect(this.padSubmix);
    p1.start(now);
    this.padOsc1 = p1;

    const p2 = this.ctx.createOscillator();
    p2.type = 'triangle';
    p2.frequency.setValueAtTime(this.profile.baseFrequency, now);
    p2.detune.setValueAtTime(9, now);
    p2.connect(this.padSubmix);
    p2.start(now);
    this.padOsc2 = p2;

    const padLfo = this.ctx.createOscillator();
    padLfo.type = 'sine';
    padLfo.frequency.setValueAtTime(0.16, now);
    padLfo.connect(this.padFilterLfoGain);
    this.padFilterLfoGain.connect(this.padFilter.frequency);
    padLfo.start(now);
    this.padFilterLfo = padLfo;

    // 4. Layer 4: EV_CHOIR (5 Detuned Voices: -12, -7, 0, +7, +12 cents)
    this.choirOscs = [];
    this.choirGains = [];
    const choirDetunes = [-12, -7, 0, 7, 12];
    choirDetunes.forEach((cents) => {
      const v = this.ctx.createOscillator();
      v.type = 'sine';
      v.frequency.setValueAtTime(this.profile.baseFrequency * 2.0, now);
      v.detune.setValueAtTime(cents, now);

      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0, now);

      v.connect(g);
      g.connect(this.choirSubmix);
      v.start(now);

      this.choirOscs.push(v);
      this.choirGains.push(g);
    });

    const choirLfo = this.ctx.createOscillator();
    choirLfo.type = 'sine';
    choirLfo.frequency.setValueAtTime(0.85, now);
    choirLfo.connect(this.choirLfoGain);
    this.choirLfoGain.connect(this.choirSubmix.gain);
    choirLfo.start(now);
    this.choirLfo = choirLfo;

    // 5. Layer 5: EV_AIR Looping Pink Noise Buffer
    const bufferSize = this.ctx.sampleRate * 2;
    const noiseBuffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99 * b0 + white * 0.05;
      b1 = 0.96 * b1 + white * 0.11;
      b2 = 0.86 * b2 + white * 0.25;
      data[i] = (b0 + b1 + b2 + white * 0.1) * 0.40;
    }
    const airSource = this.ctx.createBufferSource();
    airSource.buffer = noiseBuffer;
    airSource.loop = true;
    airSource.connect(this.airFilter);
    airSource.start(now);
    this.airNoiseSource = airSource;

    // 6. Layer 6: EV_ACCELERATION Shimmer
    const acc = this.ctx.createOscillator();
    acc.type = 'sawtooth';
    acc.frequency.setValueAtTime(this.profile.baseFrequency * 3.5, now);
    acc.connect(this.accelFilter);
    acc.start(now);
    this.accelOsc = acc;

    // 7. Layer 7: EV_REGEN Electrical & Descending Tone
    const reg = this.ctx.createOscillator();
    reg.type = 'triangle';
    reg.frequency.setValueAtTime(this.profile.baseFrequency * 1.5, now);
    reg.connect(this.regenFilter);
    reg.start(now);
    this.regenOsc = reg;

    // 8. Dynamic Pulse AM LFO
    const pLfo = this.ctx.createOscillator();
    pLfo.type = 'sine';
    pLfo.frequency.setValueAtTime(1.2, now);
    pLfo.connect(this.pulseLfoGain);
    this.pulseLfoGain.connect(this.pulseModGain.gain);
    pLfo.start(now);
    this.pulseLfo = pLfo;

    // 9. Relax Mode Soothing Massage LFO (~0.2 Hz)
    const rLfo = this.ctx.createOscillator();
    rLfo.type = 'sine';
    rLfo.frequency.setValueAtTime(0.20, now);
    rLfo.connect(this.relaxMassageGain);
    this.relaxMassageGain.connect(this.coreGain.gain);
    rLfo.start(now);
    this.relaxMassageLfo = rLfo;

    // 10. Layer 8: EV_INVERTER (Singing Inverter & Plasma Overtones)
    const invOsc = this.ctx.createOscillator();
    invOsc.type = 'triangle';
    invOsc.frequency.setValueAtTime(this.profile.baseFrequency * 6.0, now);
    invOsc.detune.setValueAtTime(7, now);
    invOsc.connect(this.inverterGain);
    invOsc.start(now);
    this.inverterOsc = invOsc;

    const plasOsc = this.ctx.createOscillator();
    plasOsc.type = 'sine';
    plasOsc.frequency.setValueAtTime(this.profile.baseFrequency * 8.0, now);
    plasOsc.detune.setValueAtTime(-5, now);
    plasOsc.connect(this.inverterGain);
    plasOsc.start(now);
    this.plasmaOsc = plasOsc;
  }

  private cleanupNodes(): void {
    if (this.subOsc) {
      try { this.subOsc.stop(); this.subOsc.disconnect(); } catch {}
      this.subOsc = null;
    }
    if (this.coreOsc1) {
      try { this.coreOsc1.stop(); this.coreOsc1.disconnect(); } catch {}
      this.coreOsc1 = null;
    }
    if (this.coreOsc2) {
      try { this.coreOsc2.stop(); this.coreOsc2.disconnect(); } catch {}
      this.coreOsc2 = null;
    }
    this.harmonicOscs.forEach(o => {
      try { o.stop(); o.disconnect(); } catch {}
    });
    this.harmonicOscs = [];
    this.harmonicGains = [];

    if (this.padOsc1) {
      try { this.padOsc1.stop(); this.padOsc1.disconnect(); } catch {}
      this.padOsc1 = null;
    }
    if (this.padOsc2) {
      try { this.padOsc2.stop(); this.padOsc2.disconnect(); } catch {}
      this.padOsc2 = null;
    }
    if (this.padFilterLfo) {
      try { this.padFilterLfo.stop(); this.padFilterLfo.disconnect(); } catch {}
      this.padFilterLfo = null;
    }
    this.choirOscs.forEach(o => {
      try { o.stop(); o.disconnect(); } catch {}
    });
    this.choirOscs = [];
    this.choirGains = [];

    if (this.choirLfo) {
      try { this.choirLfo.stop(); this.choirLfo.disconnect(); } catch {}
      this.choirLfo = null;
    }
    if (this.airNoiseSource) {
      try { this.airNoiseSource.stop(); this.airNoiseSource.disconnect(); } catch {}
      this.airNoiseSource = null;
    }
    if (this.accelOsc) {
      try { this.accelOsc.stop(); this.accelOsc.disconnect(); } catch {}
      this.accelOsc = null;
    }
    if (this.regenOsc) {
      try { this.regenOsc.stop(); this.regenOsc.disconnect(); } catch {}
      this.regenOsc = null;
    }
    if (this.inverterOsc) {
      try { this.inverterOsc.stop(); this.inverterOsc.disconnect(); } catch {}
      this.inverterOsc = null;
    }
    if (this.plasmaOsc) {
      try { this.plasmaOsc.stop(); this.plasmaOsc.disconnect(); } catch {}
      this.plasmaOsc = null;
    }
    if (this.pulseLfo) {
      try { this.pulseLfo.stop(); this.pulseLfo.disconnect(); } catch {}
      this.pulseLfo = null;
    }
    if (this.relaxMassageLfo) {
      try { this.relaxMassageLfo.stop(); this.relaxMassageLfo.disconnect(); } catch {}
      this.relaxMassageLfo = null;
    }
  }

  /**
   * Main real-time update loop driven directly by Vehicle Dynamics:
   * Speed + Acceleration + Throttle + Brake + Mode
   * Zero RPM • Zero Gears • Full-Bodied BMW Iconic Cinematic Acoustic Field
   */
  public update(
    speedKmh: number,
    acceleration: number,
    throttle: number,
    brake: number = 0,
    mode: DriveMode = this.driveMode,
    timestamp: number = performance.now()
  ): EVSoundState {
    this.currentSpeedKmh = Math.max(0, Math.min(260, speedKmh));
    this.currentAcceleration = Math.max(-1.0, Math.min(1.0, acceleration));
    this.currentThrottle = Math.max(0.0, Math.min(1.0, throttle));
    this.currentBrake = Math.max(0.0, Math.min(1.0, brake));
    this.driveMode = mode;

    const dtMs = this.lastUpdateTime > 0
      ? Math.min(100, Math.max(1, timestamp - this.lastUpdateTime))
      : 16.6;
    this.lastUpdateTime = timestamp;

    const now = this.ctx.currentTime;
    const timeConstant = 0.050; // 50ms smooth parameter ramp

    // 1. Non-linear musical fundamental pitch
    let basePitch = mapSpeedToPitch(this.currentSpeedKmh, this.profile);

    // 2. Acceleration / Deceleration pitch modulation
    if (this.currentAcceleration > 0) {
      const accelLift = this.currentAcceleration * 45 * this.profile.accelerationGain;
      basePitch += accelLift;
    } else if (this.currentAcceleration < 0 || this.currentBrake > 0.05) {
      const brakeEffort = Math.max(Math.abs(this.currentAcceleration), this.currentBrake);
      const decelDrop = brakeEffort * 30 * this.profile.brakingGain;
      basePitch = Math.max(40, basePitch - decelDrop);
    }
    this.currentBaseFreqHz = basePitch;

    // 3. Drive Mode Multipliers
    let modeVolumeMultiplier = 1.0;
    let modeBrightnessMultiplier = 1.0;
    let modeSpatialMultiplier = 1.0;
    let modeSensitivityMultiplier = 1.0;

    if (mode === 'ECO') {
      modeVolumeMultiplier = 0.75;
      modeBrightnessMultiplier = 0.70;
      modeSpatialMultiplier = 0.75;
      modeSensitivityMultiplier = 0.75;
    } else if (mode === 'SPORT') {
      modeVolumeMultiplier = 1.25;
      modeBrightnessMultiplier = 1.45;
      modeSpatialMultiplier = 1.30;
      modeSensitivityMultiplier = 1.35;
    }

    // 4. Raw Intensity & Asymmetric Attack/Release
    const speedRatio = Math.min(1.0, this.currentSpeedKmh / 160);
    const throttleLoad = Math.max(0.08, this.currentThrottle);
    const targetRawIntensity = (speedRatio * 0.55 + throttleLoad * 0.45) * modeVolumeMultiplier;

    const isAttack = targetRawIntensity > this.currentIntensity;
    const attackMs = this.profile.attackTimeMs || (this.profile.attackTime * 1000) || 300;
    const releaseMs = this.profile.releaseTimeMs || (this.profile.releaseTime * 1000) || 600;

    const smoothFactor = isAttack
      ? Math.min(1.0, dtMs / (attackMs / modeSensitivityMultiplier))
      : Math.min(1.0, dtMs / (releaseMs * modeSensitivityMultiplier));

    this.currentIntensity += (targetRawIntensity - this.currentIntensity) * smoothFactor;
    const intensity = Math.max(0.0, Math.min(1.0, this.currentIntensity));

    // 5. Selective Audio Highway Coasting Cut (No unnecessary drone)
    const targetCoasting = calculateCoastingAmount(
      this.currentSpeedKmh,
      this.currentAcceleration,
      this.currentThrottle,
      this.currentBrake
    );
    this.currentCoastingAmount += (targetCoasting - this.currentCoastingAmount) * 0.08;
    const coastingAmount = this.currentCoastingAmount;
    // Highway silence attenuation factor (cuts artificial drone by 85–95%)
    const coastingAtten = 1.0 - coastingAmount * this.profile.coastingReduction;

    // 6. Dynamic Harmonic Tension & THX Deep Note Resolution
    const speedDelta = Math.abs(this.currentSpeedKmh - this.lastSpeedSample);
    this.lastSpeedSample = this.currentSpeedKmh;

    if (speedDelta < 0.8 && this.currentSpeedKmh > 20 && Math.abs(this.currentAcceleration) < 0.10) {
      this.speedStabilityTimerMs += dtMs;
    } else {
      this.speedStabilityTimerMs = Math.max(0, this.speedStabilityTimerMs - dtMs * 2.0);
    }

    const accelTensionTarget = Math.max(0, this.currentAcceleration) * this.profile.accelerationSensitivity;
    this.currentTension += (accelTensionTarget - this.currentTension) * (isAttack ? 0.15 : 0.05);

    // Determine Active Chord State & Ratios
    let activeChordRatios: number[] = [...this.profile.harmonicRatios];
    let chordStateLabel = 'Harmonic Field';

    if (this.currentSpeedKmh < 4.0) {
      chordStateLabel = this.profile.id === 'ev_relax' ? 'Massage Sanctuary' : 'Warm Fundamental';
    } else if (this.currentBrake > 0.12 || this.currentAcceleration < -0.12) {
      chordStateLabel = 'Regenerative Descent';
    } else if (coastingAmount > 0.50) {
      chordStateLabel = 'Highway Glide (Drone Cut)';
    } else if (this.profile.hasChordProgression) {
      // In Expressive Mode: Build tension cluster under acceleration -> Resolve to THX Major 9th chord on steady cruise
      if (this.currentAcceleration > 0.12) {
        chordStateLabel = 'THX Tension Swell';
        // Dissonant cinematic tension cluster
        activeChordRatios = [1.0, 1.1892, 1.4142, 1.7818, 2.1189, 2.6697, 3.1748];
      } else if (this.speedStabilityTimerMs > 1000) {
        // Triumphant THX-style Major 9th resolution!
        chordStateLabel = '✨ THX Chord Resolution (Maj 9th)';
        activeChordRatios = [1.0, 1.2599, 1.4983, 2.0, 2.2449, 2.9966, 3.9959];
      } else {
        chordStateLabel = 'Expressive Cruise';
      }
    } else if (this.profile.id === 'ev_relax') {
      chordStateLabel = 'Acoustic Massage';
    } else if (this.profile.id === 'ev_sport') {
      chordStateLabel = this.currentAcceleration > 0.15 ? 'Hyper-Drive Bite' : 'Sport Stator Field';
    } else {
      chordStateLabel = 'Cinematic Field';
    }
    this.currentChordState = chordStateLabel;

    // 7. Spatial Stereo Width Modulation
    const targetSpatial = Math.min(
      1.0,
      (0.40 + (this.currentSpeedKmh / 200) * 0.45 + (this.currentThrottle * 0.25)) *
        this.profile.spatialWidth *
        modeSpatialMultiplier *
        coastingAtten
    );
    this.currentSpatialWidth += (targetSpatial - this.currentSpatialWidth) * 0.08;

    // 8. Regenerative / Braking Expression
    const isRegen = (this.currentAcceleration < -0.06 || this.currentBrake > 0.05) && this.currentSpeedKmh > 3.0;
    const regenIntensity = isRegen
      ? Math.min(1.0, Math.max(Math.abs(this.currentAcceleration), this.currentBrake))
      : 0.0;

    // 9. Dynamic Pulse AM Rate Calculation (for Pulse EV)
    const basePulse = this.profile.pulseBaseRate || 1.2;
    const pulseFactor = this.profile.pulseAccelFactor || 3.2;
    this.currentPulseRateHz = basePulse + intensity * pulseFactor;

    // -------------------------------------------------------------
    // AUTOMATE ALL SYNTHESIS LAYERS (Authoritative Full-Bodied Sound)
    // -------------------------------------------------------------
    if (this.isRunning && this.ctx.state === 'running') {
      // Layer 0: EV_SUB (0.5x Sub-Octave Cinema Bass Foundation)
      if (this.subOsc) {
        const subFreq = Math.max(25, basePitch * 0.5);
        this.subOsc.frequency.setTargetAtTime(subFreq, now, timeConstant);

        const subStandstill = this.currentSpeedKmh < 4
          ? (this.profile.id === 'ev_relax' ? 0.40 : 0.45)
          : 0.75;
        const subLevel = (0.45 + intensity * 0.55) *
          this.profile.fundamentalGain *
          subStandstill *
          coastingAtten;
        this.subGain.gain.setTargetAtTime(subLevel, now, timeConstant);

        const subCutoff = 160 + (this.currentSpeedKmh / 200) * 140;
        this.subFilter.frequency.setTargetAtTime(subCutoff, now, timeConstant);
      }

      // Layer 1: EV_CORE (1.0x Rich, Full-Bodied Fundamental)
      if (this.coreOsc1 && this.coreOsc2) {
        this.coreOsc1.frequency.setTargetAtTime(basePitch, now, timeConstant);
        this.coreOsc2.frequency.setTargetAtTime(basePitch, now, timeConstant);

        // Standstill level: warm, reassuring rumble
        const standstillFactor = this.currentSpeedKmh < 4
          ? (this.profile.id === 'ev_relax' ? 0.04 : 0.22)
          : 0.75;

        const coreLevel = (0.45 + intensity * 0.55) *
          this.profile.fundamentalGain *
          standstillFactor *
          coastingAtten;
        this.coreGain.gain.setTargetAtTime(coreLevel, now, timeConstant);

        // Warm analog-style lowpass cutoff
        const coreCutoff = 650 + (this.currentSpeedKmh / 200) * 800 * this.profile.brightness;
        this.coreFilter.frequency.setTargetAtTime(coreCutoff, now, timeConstant);
      }

      // Relax Mode Massage LFO: subtle 0.2 Hz soothing sub-bass swell
      if (this.relaxMassageLfo && this.relaxMassageGain) {
        const isRelax = this.profile.id === 'ev_relax';
        const massageDepth = isRelax ? 0.12 * (0.4 + intensity * 0.6) : 0.0;
        this.relaxMassageGain.gain.setTargetAtTime(massageDepth, now, timeConstant);
      }

      // Layer 2: EV_HARMONICS Bank
      const submixGain = 0.85 * this.profile.harmonicGain * modeBrightnessMultiplier * coastingAtten;
      this.harmonicsSubmix.gain.setTargetAtTime(submixGain, now, timeConstant);

      for (let i = 0; i < this.harmonicOscs.length; i++) {
        const osc = this.harmonicOscs[i];
        const gainNode = this.harmonicGains[i];
        if (!osc || !gainNode) continue;

        const ratio = activeChordRatios[i] || (i + 1);
        const hFreq = Math.min(15000, basePitch * ratio);
        // Smooth portamento during chord resolution
        const freqGlideTime = this.profile.hasChordProgression && this.speedStabilityTimerMs > 800 ? 0.35 : timeConstant;
        osc.frequency.setTargetAtTime(hFreq, now, freqGlideTime);

        if (i < activeChordRatios.length) {
          let hGain = (0.35 / Math.sqrt(i + 1)) *
            (0.25 + intensity * 0.75) *
            this.profile.harmonicGain *
            modeBrightnessMultiplier *
            coastingAtten;

          // Upper harmonics swell strongly during tension and acceleration
          if (i >= 3) {
            hGain *= Math.max(0.2, this.currentTension * 1.8);
          }
          if (this.currentSpeedKmh < 3) hGain *= 0.20;

          gainNode.gain.setTargetAtTime(hGain, now, timeConstant);
        } else {
          gainNode.gain.setTargetAtTime(0.0, now, timeConstant);
        }
      }

      // Layer 3: EV_PAD (Cinematic Detuned Wall-of-Sound - 0.5x / 1.0x Pitch)
      if (this.padOsc1 && this.padOsc2) {
        this.padOsc1.frequency.setTargetAtTime(basePitch * 0.5, now, timeConstant);
        this.padOsc2.frequency.setTargetAtTime(basePitch * 0.5, now, timeConstant);

        const padStandstill = this.currentSpeedKmh < 3 ? 0.30 : 0.85;
        const padPresence = Math.max(padStandstill, Math.min(1.0, this.currentSpeedKmh / 40));
        const padLevel = padPresence *
          this.profile.padGain *
          (0.35 + intensity * 0.65) *
          coastingAtten;
        this.padSubmix.gain.setTargetAtTime(padLevel, now, timeConstant);

        const padCutoff = 600 + (this.currentSpeedKmh / 200) * 1200 * this.profile.brightness;
        this.padFilter.frequency.setTargetAtTime(padCutoff, now, timeConstant);
      }

      // Layer 4: EV_CHOIR (5 Detuned Voices with Formant Sweeps - 2.0x Pitch)
      this.choirOscs.forEach((osc) => {
        osc.frequency.setTargetAtTime(basePitch * 2.0, now, timeConstant);
      });
      const choirLevel = Math.max(0.05, (this.currentSpeedKmh) / 75) *
        this.profile.choirGain *
        (0.30 + intensity * 0.70) *
        coastingAtten *
        0.60;
      this.choirSubmix.gain.setTargetAtTime(choirLevel, now, timeConstant);

      const f1 = 650 + (this.currentSpeedKmh / 200) * 180;
      const f2 = 1750 + (this.currentSpeedKmh / 200) * 350;
      this.choirFormant1.frequency.setTargetAtTime(f1, now, timeConstant);
      this.choirFormant2.frequency.setTargetAtTime(f2, now, timeConstant);

      // Layer 5: EV_AIR Texture
      const airTargetGain = (this.currentSpeedKmh / 200) *
        this.profile.airGain *
        0.30 *
        coastingAtten;
      this.airGain.gain.setTargetAtTime(airTargetGain, now, timeConstant);
      const airCutoff = 1800 + (this.currentSpeedKmh / 200) * 2400;
      this.airFilter.frequency.setTargetAtTime(airCutoff, now, timeConstant);

      // Layer 6: EV_ACCELERATION Shimmer & Visceral Bite (3.8x Pitch)
      if (this.accelOsc) {
        if (this.currentAcceleration > 0.06 && this.currentThrottle > 0.10 && coastingAmount < 0.25) {
          this.accelOsc.frequency.setTargetAtTime(basePitch * 3.8, now, timeConstant);
          const shimmerGain = this.currentAcceleration *
            this.profile.accelerationGain *
            0.42 *
            modeBrightnessMultiplier *
            modeSensitivityMultiplier;
          this.accelGain.gain.setTargetAtTime(shimmerGain, now, timeConstant);
        } else {
          this.accelGain.gain.setTargetAtTime(0.0, now, timeConstant);
        }
      }

      // Layer 7: EV_REGEN / EV_BRAKE Electrical Deceleration Tone
      if (this.regenOsc) {
        if (regenIntensity > 0.04) {
          const regenFreq = Math.max(80, basePitch * 1.45 * (1.0 - regenIntensity * 0.25));
          this.regenOsc.frequency.setTargetAtTime(regenFreq, now, timeConstant);

          const regenLevel = regenIntensity * this.profile.brakingGain * 0.50;
          this.regenGain.gain.setTargetAtTime(regenLevel, now, timeConstant);
          this.regenFilter.frequency.setTargetAtTime(700 + regenIntensity * 500, now, timeConstant);
        } else {
          this.regenGain.gain.setTargetAtTime(0.0, now, timeConstant);
        }
      }

      // Layer 8: EV_INVERTER (High-Frequency Plasma Turbine & Singing Inverter - 6.0x-8.0x Pitch)
      if (this.inverterOsc && this.plasmaOsc) {
        const invFreq = Math.min(12000, basePitch * 6.0);
        const plasFreq = Math.min(16000, basePitch * 8.0);
        this.inverterOsc.frequency.setTargetAtTime(invFreq, now, timeConstant);
        this.plasmaOsc.frequency.setTargetAtTime(plasFreq, now, timeConstant);

        const invCenterFreq = 2200 + (this.currentSpeedKmh / 200) * 2600;
        this.inverterFilter.frequency.setTargetAtTime(invCenterFreq, now, timeConstant);

        const invStandstill = this.currentSpeedKmh < 4 ? 0.08 : 0.45;
        const invLevel = (invStandstill + intensity * 0.55) *
          this.profile.brightness *
          modeBrightnessMultiplier *
          coastingAtten *
          0.38;
        this.inverterGain.gain.setTargetAtTime(invLevel, now, timeConstant);
      }

      // Layer E: Dynamic Pulse Modulation
      if (this.pulseLfo) {
        this.pulseLfo.frequency.setTargetAtTime(this.currentPulseRateHz, now, timeConstant);
        const pulseDepth = this.profile.id === 'ev_pulse' ? 0.42 * (0.35 + intensity * 0.65) : 0.0;
        this.pulseLfoGain.gain.setTargetAtTime(pulseDepth, now, timeConstant);
      }

      // Full-Bodied Master Synth Output with Coasting Attenuation
      const masterVol = (0.78 + intensity * 0.35) * modeVolumeMultiplier * coastingAtten;
      this.masterGain.gain.setTargetAtTime(masterVol, now, timeConstant);
    }

    return {
      speedKmh: this.currentSpeedKmh,
      acceleration: this.currentAcceleration,
      throttle: this.currentThrottle,
      mode: this.driveMode,
      intensity,
      pitchHz: Math.round(basePitch * 10) / 10,
      spatialWidth: Math.round(this.currentSpatialWidth * 100) / 100,
      regenIntensity: Math.round(regenIntensity * 100) / 100,
    };
  }

  /**
   * Switch EV Profile cleanly with zero-click AudioParam crossfade
   */
  public setProfile(profile: EVSoundProfile): void {
    this.profile = profile;
    const now = this.ctx.currentTime;
    const crossfadeDuration = 0.45;

    if (this.isRunning && this.ctx.state === 'running') {
      if (this.subOsc) {
        this.subOsc.frequency.setTargetAtTime(this.profile.baseFrequency * 0.5, now, crossfadeDuration);
      }
      if (this.coreOsc1 && this.coreOsc2) {
        this.coreOsc1.frequency.setTargetAtTime(this.profile.baseFrequency, now, crossfadeDuration);
        this.coreOsc2.frequency.setTargetAtTime(this.profile.baseFrequency, now, crossfadeDuration);
      }
      if (this.inverterOsc) {
        this.inverterOsc.frequency.setTargetAtTime(this.profile.baseFrequency * 6.0, now, crossfadeDuration);
      }
      if (this.plasmaOsc) {
        this.plasmaOsc.frequency.setTargetAtTime(this.profile.baseFrequency * 8.0, now, crossfadeDuration);
      }

      for (let i = 0; i < this.harmonicOscs.length; i++) {
        const osc = this.harmonicOscs[i];
        if (osc) {
          const ratio = this.profile.harmonicRatios[i] || (i + 1);
          osc.frequency.setTargetAtTime(this.profile.baseFrequency * ratio, now, crossfadeDuration);
        }
      }

      this.update(
        this.currentSpeedKmh,
        this.currentAcceleration,
        this.currentThrottle,
        this.currentBrake,
        this.driveMode,
        performance.now()
      );
    }
  }

  /**
   * Switch Driving Mode with smooth crossfade
   */
  public setMode(mode: DriveMode): void {
    this.driveMode = mode;
    if (this.isRunning && this.ctx.state === 'running') {
      this.update(
        this.currentSpeedKmh,
        this.currentAcceleration,
        this.currentThrottle,
        this.currentBrake,
        this.driveMode,
        performance.now()
      );
    }
  }

  /**
   * Real-time EV Musical State snapshot
   */
  public getMusicalState(): EVMusicalState {
    const normSpeed = Math.min(1.0, this.currentSpeedKmh / 200);
    return {
      speedKmh: Math.round(this.currentSpeedKmh * 10) / 10,
      acceleration: Math.round(this.currentAcceleration * 100) / 100,
      throttle: Math.round(this.currentThrottle * 100) / 100,
      brake: Math.round(this.currentBrake * 100) / 100,
      speedNormalized: Math.round(normSpeed * 100) / 100,
      intensity: Math.round(this.currentIntensity * 100) / 100,
      tension: Math.round(this.currentTension * 100) / 100,
      fundamentalFrequency: Math.round(this.currentBaseFreqHz * 10) / 10,
      harmonicIntensity: Math.round(this.profile.harmonicGain * this.currentIntensity * 100) / 100,
      brightness: Math.round(this.profile.brightness * 100) / 100,
      coastingAmount: Math.round(this.currentCoastingAmount * 100) / 100,
      brakingIntensity: Math.round(this.currentBrake * 100) / 100,
      chordState: this.currentChordState,
      spatialWidth: Math.round(this.currentSpatialWidth * 100) / 100,
      pulseRateHz: Math.round(this.currentPulseRateHz * 10) / 10,
    };
  }

  public getDebugInfo(): EVSoundDebugInfo {
    return {
      speedKmh: Math.round(this.currentSpeedKmh * 10) / 10,
      baseFrequencyHz: Math.round(this.currentBaseFreqHz * 10) / 10,
      intensityPct: Math.round(this.currentIntensity * 100),
      spatialWidthPct: Math.round(this.currentSpatialWidth * 100),
      regenIntensityPct: Math.round(
        (this.currentAcceleration < -0.05 || this.currentBrake > 0.05
          ? Math.max(Math.abs(this.currentAcceleration), this.currentBrake)
          : 0) * 100
      ),
      coreGain: Math.round(this.coreGain.gain.value * 100) / 100,
      harmonicsGain: Math.round(this.harmonicsSubmix.gain.value * 100) / 100,
      choirGain: Math.round(this.choirSubmix.gain.value * 100) / 100,
      padGain: Math.round(this.padSubmix.gain.value * 100) / 100,
      airGain: Math.round(this.airGain.gain.value * 100) / 100,
      accelerationGain: Math.round(this.accelGain.gain.value * 100) / 100,
      regenGain: Math.round(this.regenGain.gain.value * 100) / 100,
      spatialGain: Math.round(this.currentSpatialWidth * 100) / 100,
      activeMode: this.driveMode,
      chordState: this.currentChordState,
      pulseRateHz: Math.round(this.currentPulseRateHz * 10) / 10,
      coastingPct: Math.round(this.currentCoastingAmount * 100),
      subGain: Math.round(this.subGain.gain.value * 100) / 100,
      inverterGain: Math.round(this.inverterGain.gain.value * 100) / 100,
    };
  }

  public destroy(): void {
    this.stop();
  }
}
